import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ExportType, JobStatus } from 'cli/enums';
import { useGenerateExport, useJobChecker } from 'cli/hooks';
import { ICliJob } from 'cli/types';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';

// Every handleCliJob caller acts on a quote or an order: what a lost job may have changed.
export const LOST_CLI_JOB_QUERY_KEYS = ['quote', 'customerQuoteParts', 'vendorQuoteParts'];

export interface ICliJobLostPoll {
  cliJob: ICliJob;
  error?: unknown;
  // Replaces the in-progress notification with the lost-track notice, at the caller's position.
  showLostTrackNotice: () => void;
}

interface ICliJobHandlerOptions {
  cliJobInProgressMessage?: string;
  cliJobErrorMessage?: string;
  onCliJobFinished?: (cliJob: ICliJob) => void;
  onCliJobStopped?: (cliJob: ICliJob) => void;
  // The job's end is unknown: its status checks kept failing. Replaces the default (refetch the
  // quote queries, then the lost-track notice); the caller refetches its entity and decides.
  onCliJobPollFailed?: (lostPoll: ICliJobLostPoll) => void;
  // Each poll that finds the job still pending (queued or running).
  onCliJobProgress?: (cliJob: ICliJob) => void;
  notificationPosition?: NotificationPosition;
  // Opt-in: surface the BE error copy (error.response.data.error) over the generic fallback.
  // Only the Send-as-SPA callers set this; other consumers keep their prior generic message.
  preferBackendError?: boolean;
}

interface IExportHandlerOptions {
  exportInProgressMessage?: string;
  exportSuccessMessage?: string;
  exportErrorMessage?: string;
  onGenerateExportFinished?: (cliJob: ICliJob) => void;
}

export const useCliJobHandler = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { checkJobStatus } = useJobChecker();
  const { onGenerateExportFinished, onGenerateExportStopped } = useGenerateExport();

  // Resolves true once job polling has started, false when the job never started (the
  // mutation failed) — callers holding an in-flight flag reset it on false.
  const handleCliJob = async (
    jobAction,
    cliJobPayload,
    options: ICliJobHandlerOptions = {}
  ): Promise<boolean> => {
    const {
      cliJobInProgressMessage,
      cliJobErrorMessage,
      onCliJobFinished = onCliJobEnd,
      onCliJobStopped = onCliJobEnd,
      onCliJobPollFailed,
      onCliJobProgress,
      notificationPosition = NotificationPosition.TOP,
      preferBackendError = false,
    } = options;

    if (cliJobInProgressMessage) {
      showNotification({
        type: ApiStatus.LOADING,
        title: t('titleStatus'),
        message: cliJobInProgressMessage,
        position: notificationPosition,
      });
    }

    try {
      const data = await jobAction(cliJobPayload);

      checkJobStatus(
        data,
        (cliJob) => onCliJobFinished(cliJob, notificationPosition),
        (cliJob) => onCliJobStopped(cliJob, notificationPosition),
        {
          notificationPosition,
          onError: (error?: unknown) => {
            const showLostTrackNotice = () =>
              showWarning(t('messageCliJobLostTrack'), notificationPosition);

            if (onCliJobPollFailed) {
              onCliJobPollFailed({ cliJob: data, error, showLostTrackNotice });

              return;
            }

            LOST_CLI_JOB_QUERY_KEYS.forEach((queryKey) => queryClient.invalidateQueries(queryKey));
            showLostTrackNotice();
          },
          onProgress: onCliJobProgress,
        }
      );

      return true;
    } catch (error) {
      // Surface the BE error copy (e.g. Send-as-SPA "not configured" / bulk
      // "no eligible quotes" 422s) over the caller's generic fallback — opt-in per
      // caller so the other consumers keep their prior generic message.
      const backendError = preferBackendError
        ? (error as { response?: { data?: { error?: string } } })?.response?.data?.error
        : undefined;

      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message:
          backendError || cliJobErrorMessage || (error as Error)?.message || t('serverError'),
        position: notificationPosition,
      });

      return false;
    }
  };

  const handleCliJobExport = async (
    exportAction,
    exportPayload,
    options?: IExportHandlerOptions,
    exportType: ExportType = ExportType.FILE
  ) => {
    const defaultOptions = (error = null) => ({
      exportInProgressMessage: t('messageGenerateExportInProgress'),
      exportSuccessMessage: t('messageGenerateExportSuccess'),
      // Surface the BE error copy (e.g. bulk export 422 validation) over axios' generic message.
      exportErrorMessage: error?.response?.data?.error || error?.message || t('serverError'),
      ...options,
    });

    showNotification({
      type: ApiStatus.LOADING,
      title: t('titleStatus'),
      message: defaultOptions().exportInProgressMessage,
      position: NotificationPosition.TOP,
    });

    try {
      const data = await exportAction(exportPayload);

      checkJobStatus(
        data,
        (cliJob) => onGenerateExportFinished(exportType, cliJob, options),
        (cliJob) => onGenerateExportStopped(cliJob),
        // No entity to refetch: the user starts the download again.
        { onError: () => showWarning(t('messageExportLostTrack'), NotificationPosition.TOP) }
      );
    } catch (error) {
      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message: defaultOptions(error).exportErrorMessage,
        position: NotificationPosition.TOP,
      });
    }
  };

  const showWarning = (message: string, notificationPosition: NotificationPosition) =>
    showNotification({
      type: ApiStatus.WARNING,
      title: t('titleWarning'),
      message,
      position: notificationPosition,
    });

  const onCliJobEnd = (cliJob: ICliJob, notificationPosition = NotificationPosition.TOP) => {
    let type: ApiStatus;
    let message: string;
    let title: string;

    switch (cliJob.status) {
      case JobStatus.FINISHED:
        title = t('titleSuccess');
        type = ApiStatus.SUCCESS;
        message = t('messageCliJobFinished');
        break;
      default:
        title = t('titleError');
        type = ApiStatus.ERROR;
        message = t('messageCliJobInterrupted', {
          description: cliJob.statusDescription || cliJob.status,
        });
        break;
    }

    showNotification({
      type,
      title,
      message: message,
      position: notificationPosition,
    });
  };

  return {
    handleCliJob,
    handleCliJobExport,
  };
};
